import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Send, Paperclip, Loader2, Home, Users, X, Trash2, Shield, ArrowLeft, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolve = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);
const fmtTime = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const initials = (name) => (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const Avatar = ({ name, photo, size = 9 }) => (
  photo ? <img src={resolve(photo)} alt={name} className={`h-${size} w-${size} rounded-full object-cover shrink-0`} />
    : <div className={`h-${size} w-${size} rounded-full bg-[#0F2744] text-white text-xs font-bold flex items-center justify-center shrink-0`}>{initials(name)}</div>
);

export default function Community() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [unread, setUnread] = useState({});           // group unread map
  const [active, setActive] = useState(null);         // {type:'group'|'dm', id, name, photo, online}
  const [messages, setMessages] = useState([]);
  const [range, setRange] = useState("all");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  // presence heartbeat
  useEffect(() => {
    const ping = () => api.post("/presence/ping").catch(() => {});
    ping();
    const t = setInterval(ping, 30000);
    return () => clearInterval(t);
  }, []);

  const loadSidebar = useCallback(() => {
    api.get("/chat/groups").then(({ data }) => setGroups(data)).catch(() => {});
    api.get("/chat/members").then(({ data }) => setMembers(data)).catch(() => {});
    api.get("/chat/unread").then(({ data }) => setUnread(data)).catch(() => {});
  }, []);

  useEffect(() => { loadSidebar(); const t = setInterval(loadSidebar, 10000); return () => clearInterval(t); }, [loadSidebar]);

  const load = useCallback(() => {
    if (!active) return;
    const base = active.type === "group" ? `/chat/groups/${active.id}` : `/chat/dm/${active.id}`;
    api.get(`${base}/messages`, { params: { range } }).then(({ data }) => setMessages(data)).catch(() => {});
    api.post(`${base}/read`).then(loadSidebar).catch(() => {});
  }, [active, range, loadSidebar]);

  useEffect(() => { setMessages([]); load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if ((!text.trim() && !file) || !active) return;
    setSending(true);
    try {
      let attachment_url = "", attachment_name = "";
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment_url = data.url; attachment_name = data.name;
      }
      const base = active.type === "group" ? `/chat/groups/${active.id}` : `/chat/dm/${active.id}`;
      await api.post(`${base}/messages`, { text, attachment_url, attachment_name });
      setText(""); setFile(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/chat/messages/${id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const isImg = (m) => m.attachment_url && /\.(png|jpe?g|webp|gif)$/i.test(m.attachment_name || "");
  const isDM = active?.type === "dm";

  return (
    <div className="h-screen flex flex-col bg-slate-50" data-testid="community-page">
      <header className="bg-[#0F2744] shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white font-bold"><Users className="h-5 w-5 text-amber-400" /> Member Community</div>
          <div className="flex gap-1">
            <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10"><Link to="/member" data-testid="community-back-btn">My Area</Link></Button>
            <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10"><Link to="/"><Home className="h-4 w-4" /></Link></Button>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 max-w-7xl w-full mx-auto md:px-6 lg:px-8 md:py-6 grid md:grid-cols-[320px_1fr] md:gap-4">
        {/* Sidebar */}
        <aside className={`min-h-0 flex flex-col bg-white md:border md:border-slate-200 md:rounded-2xl overflow-hidden ${active ? "hidden md:flex" : "flex"}`} data-testid="chat-sidebar">
          <div className="overflow-y-auto flex-1 p-2 space-y-4">
            <div>
              <div className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">Groups</div>
              <div className="space-y-0.5" data-testid="chat-group-list">
                {groups.map((g) => (
                  <button key={g.key} onClick={() => setActive({ type: "group", id: g.key, name: g.name })} data-testid={`chat-group-${g.key}`}
                    className={`w-full flex items-center gap-3 text-left px-2.5 py-2.5 rounded-xl transition-colors ${active?.type === "group" && active.id === g.key ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                    <div className="h-11 w-11 rounded-full bg-[#0F2744] text-white flex items-center justify-center shrink-0"><Users className="h-5 w-5 text-amber-400" /></div>
                    <span className="flex-1 min-w-0 font-semibold text-sm text-[#0F2744] truncate">{g.name}</span>
                    {unread[g.key] > 0 && <span data-testid={`chat-unread-${g.key}`} className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">{unread[g.key]}</span>}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                Members
                <span className="text-emerald-500">· {members.filter((m) => m.online).length} online</span>
              </div>
              <div className="space-y-0.5" data-testid="chat-member-list">
                {members.length === 0 && <p className="px-2.5 py-2 text-sm text-slate-400">No other members yet.</p>}
                {members.map((m) => (
                  <button key={m.id} onClick={() => setActive({ type: "dm", id: m.id, name: m.name, photo: m.photo, online: m.online })} data-testid={`chat-dm-${m.id}`}
                    className={`w-full flex items-center gap-3 text-left px-2.5 py-2.5 rounded-xl transition-colors ${active?.type === "dm" && active.id === m.id ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                    <div className="relative shrink-0">
                      <Avatar name={m.name} photo={m.photo} size={11} />
                      <span data-testid={`member-status-dot-${m.id}`} className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white ${m.online ? "bg-emerald-500" : "bg-slate-300"}`} title={m.online ? "Online" : "Offline"} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#0F2744] truncate flex items-center gap-1.5">
                        {m.name}
                        {m.role === "admin" && <Shield className="h-3 w-3 text-amber-500" />}
                      </div>
                      <div className={`text-xs ${m.online ? "text-emerald-600" : "text-slate-400"}`}>{m.online ? "Online" : "Offline"}</div>
                    </div>
                    {m.unread > 0 && <span data-testid={`dm-unread-${m.id}`} className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">{m.unread}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Conversation pane */}
        <section className={`min-h-0 flex-col bg-white md:border md:border-slate-200 md:rounded-2xl overflow-hidden ${active ? "flex" : "hidden md:flex"}`}>
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400 p-8" data-testid="chat-empty-state">
              <MessageCircle className="h-12 w-12 mb-3 text-slate-300" />
              <p className="font-semibold text-slate-500">Your community chats</p>
              <p className="text-sm mt-1">Pick a group or a member to start chatting.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-slate-100 px-3 py-2.5 shrink-0">
                <button className="md:hidden text-slate-500 p-1" onClick={() => setActive(null)} data-testid="chat-back-mobile"><ArrowLeft className="h-5 w-5" /></button>
                {isDM ? (
                  <div className="relative">
                    <Avatar name={active.name} photo={active.photo} size={10} />
                    <span className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white ${active.online ? "bg-emerald-500" : "bg-slate-300"}`} />
                  </div>
                ) : (
                  <div className="h-10 w-10 rounded-full bg-[#0F2744] text-white flex items-center justify-center"><Users className="h-5 w-5 text-amber-400" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0F2744] truncate" data-testid="chat-active-title">{active.name}</div>
                  <div className="text-xs text-slate-400">{isDM ? (active.online ? "Online" : "Direct message") : "Group chat"}</div>
                </div>
                <div className="flex items-center gap-1">
                  {!isDM && user?.role === "admin" && <ManageMembers key={active.id} groupKey={active.id} groupName={active.name} />}
                  <div className="flex gap-1" data-testid="chat-range-toggle">
                    {[["all", "All"], ["7", "7d"], ["30", "30d"]].map(([v, l]) => (
                      <button key={v} onClick={() => setRange(v)} data-testid={`chat-range-${v}`}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${range === v ? "bg-[#0F2744] text-white" : "bg-slate-100 text-slate-500"}`}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#efeae2]/40" data-testid="chat-messages">
                {messages.length === 0 && <p className="text-center text-sm text-slate-400 mt-8">No messages yet. Say hello 👋</p>}
                {messages.map((m) => {
                  const mine = m.user_id === user?.id;
                  return (
                    <div key={m.id} className={`flex gap-2 group ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
                      {!isDM && !mine && <Avatar name={m.user_name} photo={m.user_photo} />}
                      <div className={`max-w-[78%] rounded-2xl px-3 py-2 shadow-sm ${mine ? "bg-[#d9fdd3] rounded-tr-sm" : "bg-white rounded-tl-sm"}`}>
                        {!isDM && !mine && (
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="font-bold text-xs text-[#0F2744]">{m.user_name}</span>
                            {m.user_role === "admin" && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-600"><Shield className="h-2.5 w-2.5" />ADMIN</span>}
                          </div>
                        )}
                        {m.text && <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{m.text}</p>}
                        {m.attachment_url && (isImg(m)
                          ? <a href={resolve(m.attachment_url)} target="_blank" rel="noreferrer"><img src={resolve(m.attachment_url)} alt={m.attachment_name} className="mt-1 max-h-56 rounded-lg" /></a>
                          : <a href={resolve(m.attachment_url)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"><Paperclip className="h-3.5 w-3.5" />{m.attachment_name}</a>)}
                        <div className={`flex items-center gap-1.5 mt-0.5 ${mine ? "justify-end" : ""}`}>
                          <span className="text-[10px] text-slate-400">{fmtTime(m.created_at)}</span>
                          {(user?.role === "admin" || mine) && (
                            <button onClick={() => del(m.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500" data-testid={`chat-del-${m.id}`}><Trash2 className="h-3 w-3" /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              <form onSubmit={send} className="border-t border-slate-100 p-3 shrink-0 bg-white">
                {file && <div className="flex items-center gap-2 text-xs text-slate-500 mb-2"><Paperclip className="h-3.5 w-3.5" />{file.name}<button type="button" onClick={() => setFile(null)}><X className="h-3.5 w-3.5" /></button></div>}
                <div className="flex items-center gap-2">
                  <label className="h-10 w-10 shrink-0 rounded-full border border-slate-200 flex items-center justify-center cursor-pointer hover:bg-slate-50" title="Attach a file">
                    <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="chat-file-input" />
                    <Paperclip className="h-4 w-4 text-slate-500" />
                  </label>
                  <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message…" className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2744]/20" data-testid="chat-input" />
                  <button type="submit" disabled={sending} className="h-10 w-10 shrink-0 rounded-full bg-amber-500 hover:bg-amber-600 text-[#0F2744] flex items-center justify-center disabled:opacity-50" data-testid="chat-send-btn">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function ManageMembers({ groupKey, groupName }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/admin/members").then(({ data }) => setMembers(data)).catch(() => {});
    api.get(`/admin/chat/groups/${groupKey}/members`).then(({ data }) => setSelected(data.member_ids || [])).catch(() => {});
  }, [open, groupKey]);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const save = async () => {
    try { await api.put(`/admin/chat/groups/${groupKey}/members`, { member_ids: selected }); toast.success("Authorised members updated"); setOpen(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="shrink-0" data-testid="chat-manage-members-btn"><Users className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Members</span></Button></DialogTrigger>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Authorise members — {groupName}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Open chats (Community, Items for Sale) are visible to all members. Committee chat is committee/admin only. Use this list for restricted chats like Group Ride.</p>
        <div className="space-y-1 mt-2">
          {members.map((m) => (
            <label key={m.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 text-sm" data-testid={`chat-member-opt-${m.id}`}>
              <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} />
              <span className="font-semibold text-[#0F2744]">{m.name}</span>
              <span className="text-slate-400">{m.email}</span>
            </label>
          ))}
        </div>
        <Button onClick={save} className="mt-3 bg-[#0F2744] hover:bg-[#0B192C]" data-testid="chat-members-save">Save</Button>
      </DialogContent>
    </Dialog>
  );
}
